import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { enablePaginationOnGetSegments } from '@gonfalon/dogfood-flags/dogfood-flags';
// oxlint-disable-next-line no-restricted-imports
import { fromJS, Map } from '@gonfalon/immutable-shim/immutableShim';
import type { ImmutableServerError } from '@gonfalon/legacy-http-client/httpUtils';
import { toSegments, toSegmentTargeting } from '@gonfalon/navigator/navigators';
import { type GetSegmentsQueryParams } from '@gonfalon/openapi/operations/getSegments';
import { isRESTAPIError } from '@gonfalon/rest-api/errors';
import { useCreateSegment } from '@gonfalon/rest-api/hooks/useCreateSegment';
import { useDeleteSegment } from '@gonfalon/rest-api/hooks/useDeleteSegment';
import { useSegments } from '@gonfalon/rest-api/hooks/useSegments';
import { useUpdateSegment } from '@gonfalon/rest-api/hooks/useUpdateSegment';
import { snackbarQueue } from '@launchpad-ui/components';

import {
  createSegmentDone,
  createSegmentFailed,
  createSegmentStart,
  deleteSegmentDone,
  deleteSegmentFailed,
  deleteSegmentStart,
  fetchSegmentsDone,
  fetchSegmentsFailed,
  fetchSegmentsStart,
} from '#actions/segments';
import { useDispatch } from '#hooks/useDispatch';
import { useSelector } from '#hooks/useSelector';
import { segmentTargetingManagerSelector } from '#reducers/segments';
import { normalizeGetSegmentsResponse } from '#sources/SegmentAPI';
import type { SegmentSourceType } from '#utils/segment/constants';
import type { Segment } from '#utils/segment/records/Segment';
import { createSegment } from '#utils/segment/records/Segment';
import { serializeSegmentForAPI } from '#utils/segment/serializeSegmentForAPI';

/**
 *
 * There are a handful of redux interactions in this file, because segment form state still lives there and we depend
 * on segments in redux elsewhere in the app.
 * All notifications are dispatched from redux because they also update the store. Eventually they should all
 * be updated to dispatch a snackbar instead.
 */

const useGetSegmentsQuery = ({
  projectKey,
  environmentKey,
  params,
  enabled,
  apiVersion,
}: {
  projectKey: string;
  environmentKey: string;
  params?: GetSegmentsQueryParams;
  enabled?: boolean;
  apiVersion?: string;
}) => {
  const dispatch = useDispatch();

  const isPaginationEnabledOnGetSegments = enablePaginationOnGetSegments();
  const canUseUpdatedSegmentsApi = isPaginationEnabledOnGetSegments;

  const semanticTargetingManagerState = useSelector(segmentTargetingManagerSelector);

  const query = useSegments(
    { projectKey, environmentKey, params: canUseUpdatedSegmentsApi ? params : undefined, apiVersion },
    { enabled },
  );

  const { isSuccess, data: jsonResponse, isError, error, isFetching } = query;

  const normalizedResponse = jsonResponse ? normalizeGetSegmentsResponse(jsonResponse) : Map();

  useEffect(() => {
    if (isFetching) {
      dispatch(fetchSegmentsStart());
      return;
    }

    if (isSuccess) {
      dispatch(fetchSegmentsDone(normalizedResponse, semanticTargetingManagerState.getIn(['original', 'key'])));
      return;
    }

    if (isError) {
      dispatch(fetchSegmentsFailed(error as ImmutableServerError));
      return;
    }
  }, [isFetching, isSuccess, isError]);

  return { ...query, normalizedResponse };
};

const useCreateSegmentMutation = (source?: SegmentSourceType) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const mutation = useCreateSegment();

  const { isPending, variables } = mutation;
  const creatingSegment = variables?.body;

  useEffect(() => {
    if (isPending) {
      dispatch(createSegmentStart(createSegment(creatingSegment)));
    }
  }, [isPending]);

  const mutate: typeof mutation.mutate = (variables_) =>
    mutation.mutate(
      {
        ...variables_,
        body: serializeSegmentForAPI(createSegment(variables_.body)),
      },
      {
        onSuccess: async (_, { body: createdSegment, projectKey, environmentKey }) => {
          dispatch(createSegmentDone(createSegment(createdSegment)));
          await navigate(
            toSegmentTargeting(
              {
                projectKey,
                environmentKey,
                segmentKey: createdSegment.key,
              },
              {
                search: source ? new URLSearchParams({ 'new-source': source.toString() }) : undefined,
              },
            ),
          );
        },
        onError: (error, { body: segment }) => {
          dispatch(createSegmentFailed(createSegment(segment), fromJS(error)));
          snackbarQueue.add({
            description: isRESTAPIError(error) || error instanceof Error ? error.message : '',
            status: 'error',
          });
        },
      },
    );

  return { ...mutation, mutate };
};

const useEditSegmentSettingsMutation = () => {
  const dispatch = useDispatch();
  const segmentRef = useRef<Segment | null>(null);

  const mutation = useUpdateSegment();

  // a workaround to allow us to pass the immutable Segment record to the mutation without adding
  // it as props to the rest-api
  const mutateFn: typeof mutation.mutate = (variables_) =>
    mutation.mutate(variables_, {
      onSuccess: () => {
        dispatch({ type: 'segments/UPDATE_SEGMENT_SETTINGS_DONE', segment: segmentRef.current });
      },
      onError: (error) => {
        dispatch({ type: 'segments/UPDATE_SEGMENT_SETTINGS_FAILED', segment: segmentRef.current, error });

        if (isRESTAPIError(error)) {
          snackbarQueue.add({
            description: error.message || 'Failed to update segment settings',
            status: 'error',
          });
        }
      },
    });

  const mutate = (segment: Segment) => {
    segmentRef.current = segment;
    return mutateFn;
  };

  const { isPending } = mutation;

  useEffect(() => {
    if (isPending) {
      dispatch({ type: 'segments/UPDATE_SEGMENT_SETTINGS', segment: segmentRef.current });
      return;
    }
  }, [isPending]);

  return { ...mutation, mutate };
};

const useDeleteSegmentMutation = (segment: Segment) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const mutation = useDeleteSegment();

  const { isPending } = mutation;

  useEffect(() => {
    if (isPending) {
      dispatch(deleteSegmentStart(segment));
      return;
    }
  }, [isPending]);

  const mutate: typeof mutation.mutate = (variables) =>
    mutation.mutate(variables, {
      onSuccess: async (data, { projectKey, environmentKey }) => {
        dispatch(deleteSegmentDone(segment));
        await navigate(toSegments({ projectKey, environmentKey }));
      },
      onError: (error) => {
        dispatch(deleteSegmentFailed(segment, fromJS(error)));
        snackbarQueue.add({
          title: 'Unable to delete segment',
          description: error.message || 'Unexpected error',
          status: 'error',
        });
      },
    });

  return { ...mutation, mutate };
};

export { useGetSegmentsQuery, useCreateSegmentMutation, useEditSegmentSettingsMutation, useDeleteSegmentMutation };
